import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { CTABand } from "@/components/site/CTABand";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const FILTERS = ["All", "Monaco", "Geneva", "Paris", "Recruitment", "Talent Development", "Consulting"];

export default function CaseStudies() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  useEffect(() => { api.getCaseStudies().then(setItems).catch(() => {}); }, []);
  const filtered = useMemo(() => filter === "All" ? items : items.filter((c) => c.market === filter || c.service === filter), [filter, items]);

  return (
    <div data-testid="case-studies-page">
      <PageHero
        breadcrumb={[{ label: "Case Studies" }]}
        eyebrow="Proof, quantified"
        title="Mandates delivered — with the numbers to show for it."
        intro="Every engagement is measured. Filter by market, service or role to see how we solve specific talent challenges across Monaco, Geneva and Paris."
      />

      <section className="section-py pt-6">
        <div className="container-bp">
          <Reveal>
            <div className="bp-scroll-x -mx-[var(--gutter)] flex gap-2 overflow-x-auto px-[var(--gutter)] pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
              {FILTERS.map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`cs-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn("shrink-0 whitespace-nowrap rounded-full border px-4 py-2 text-[12.5px] font-medium transition-colors", filter === f ? "border-[color:var(--bp-teal)] bg-[color:var(--bp-teal)] text-[color:var(--bp-bg)]" : "border-[color:var(--bp-border)] text-[color:var(--bp-text-muted)] hover:border-[color:var(--bp-teal)] hover:text-[color:var(--bp-teal)]")}>
                  {f}
                </button>
              ))}
            </div>
          </Reveal>

          <RevealGroup className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((c) => (
              <RevealItem key={c.slug}>
                <Link to={`/case-studies/${c.slug}`} data-testid={`cs-card-${c.slug}`} className="group flex h-full flex-col overflow-hidden rounded-[var(--radius-lg)] border border-[color:var(--bp-border)] bg-[color:var(--bp-surface)] transition-all duration-300 hover:-translate-y-1 hover:border-[color:var(--bp-teal)]/50 hover:shadow-[var(--shadow-lift)]">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={c.image} alt={c.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute left-3 top-3 flex gap-2">
                      <span className="rounded-full bg-[color:var(--bp-bg)]/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bp-teal)] backdrop-blur">{c.market}</span>
                      <span className="rounded-full bg-[color:var(--bp-bg)]/80 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bp-cream)] backdrop-blur">{c.service}</span>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-lg font-semibold leading-snug text-[color:var(--bp-cream)]">{c.title}</h3>
                    <p className="mt-2 flex-1 text-[13.5px] leading-relaxed text-[color:var(--bp-text-muted)]">{c.summary}</p>
                    <div className="mt-5 flex items-center gap-5 border-t border-[color:var(--bp-border)] pt-4">
                      {c.metrics.slice(0, 2).map((m) => (
                        <div key={m.label}><p className="font-display text-lg font-semibold text-[color:var(--bp-gold)]">{m.value}</p><p className="text-[11px] leading-tight text-[color:var(--bp-text-muted)]">{m.label}</p></div>
                      ))}
                    </div>
                  </div>
                </Link>
              </RevealItem>
            ))}
          </RevealGroup>
        </div>
      </section>

      <CTABand title="Have a mandate like one of these?" />
    </div>
  );
}
